import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { Modal } from '../components/Modal.jsx';

const TEMPLATE_CODES = ['invoice.send', 'reminder.due', 'reminder.overdue'];

export default function EmailSettings() {
  const qc = useQueryClient();
  const { data: templates } = useQuery({ queryKey: ['templates'], queryFn: () => api('/api/email/templates') });
  const { data: rules } = useQuery({ queryKey: ['reminders'], queryFn: () => api('/api/email/reminders') });
  const [editing, setEditing] = useState(null);
  const [ruleEditing, setRuleEditing] = useState(null);

  const saveT = useMutation({ mutationFn: b => api('/api/email/templates', { method: 'PUT', body: b }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); setEditing(null); } });
  const saveR = useMutation({
    mutationFn: p => p.id ? api(`/api/email/reminders/${p.id}`, { method: 'PATCH', body: p.data }) : api('/api/email/reminders', { method: 'POST', body: p.data }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['reminders'] }); setRuleEditing(null); },
  });
  const delR = useMutation({ mutationFn: id => api(`/api/email/reminders/${id}`, { method: 'DELETE' }), onSuccess: () => qc.invalidateQueries({ queryKey: ['reminders'] }) });

  return (
    <>
      <div className="page-header"><h1>Email templates & reminders</h1></div>
      <div className="card">
        <h2>Templates</h2>
        <table className="table">
          <thead><tr><th>Code</th><th>Lang</th><th>Subject</th><th></th></tr></thead>
          <tbody>
            {TEMPLATE_CODES.flatMap(code => ['en', 'sq'].map(lang => {
              const t = templates?.templates?.find(x => x.code === code && x.language === lang);
              return (
                <tr key={`${code}-${lang}`}>
                  <td><code>{code}</code></td><td>{lang.toUpperCase()}</td>
                  <td>{t?.subject || <span className="muted">(default)</span>}</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => setEditing({ code, language: lang, subject: t?.subject || '', bodyHtml: t?.bodyHtml || '' })}>Edit</button></td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      </div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Reminder rules</h2>
          <button className="btn btn-primary btn-sm" onClick={() => setRuleEditing({})}>Add rule</button>
        </div>
        <table className="table">
          <thead><tr><th>Name</th><th>Days offset</th><th>Template</th><th>Active</th><th></th></tr></thead>
          <tbody>
            {rules?.rules?.map(r => (
              <tr key={r.id}>
                <td>{r.name}</td><td>{r.daysOffset > 0 ? `+${r.daysOffset}` : r.daysOffset}</td>
                <td><code>{r.templateCode}</code></td><td>{r.active ? 'Yes' : 'No'}</td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => setRuleEditing(r)}>Edit</button>{' '}
                  <button className="btn btn-danger btn-sm" onClick={() => window.confirm('Delete rule?') && delR.mutate(r.id)}>×</button>
                </td>
              </tr>
            ))}
            {!rules?.rules?.length && <tr><td colSpan={5} className="empty">No rules. Add e.g. "Overdue +7 days".</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={`${editing.code} · ${editing.language.toUpperCase()}`} onClose={() => setEditing(null)} wide>
          <form onSubmit={e => { e.preventDefault(); saveT.mutate(editing); }}>
            <div className="field"><label>Subject</label><input required value={editing.subject} onChange={e => setEditing(x => ({ ...x, subject: e.target.value }))} /></div>
            <div className="field"><label>Body HTML — placeholders: {'{{doc.fullNumber}}, {{doc.total}}, {{doc.dueDate}}, {{client.name}}, {{company.name}}, {{publicUrl}}'}</label>
              <textarea style={{ minHeight: 200 }} required value={editing.bodyHtml} onChange={e => setEditing(x => ({ ...x, bodyHtml: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={saveT.isPending}>Save</button>
            </div>
          </form>
        </Modal>
      )}
      {ruleEditing && (
        <Modal title={ruleEditing.id ? 'Edit rule' : 'New rule'} onClose={() => setRuleEditing(null)}>
          <RuleForm rule={ruleEditing} onSave={d => saveR.mutate({ id: ruleEditing.id, data: d })} onClose={() => setRuleEditing(null)} />
        </Modal>
      )}
    </>
  );
}

function RuleForm({ rule, onSave, onClose }) {
  const [form, setForm] = useState({
    name: rule.name || '', daysOffset: rule.daysOffset ?? 7,
    templateCode: rule.templateCode || 'reminder.overdue', active: rule.active ?? true,
  });
  return (
    <form onSubmit={e => { e.preventDefault(); onSave(form); }}>
      <div className="field"><label>Name</label><input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
      <div className="row">
        <div className="field"><label>Days offset (negative = before due, positive = after)</label>
          <input type="number" required value={form.daysOffset} onChange={e => setForm(f => ({ ...f, daysOffset: Number(e.target.value) }))} /></div>
        <div className="field"><label>Template</label>
          <select value={form.templateCode} onChange={e => setForm(f => ({ ...f, templateCode: e.target.value }))}>
            {TEMPLATE_CODES.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="field"><label><input type="checkbox" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} /> Active</label></div>
      <div className="modal-actions">
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save</button>
      </div>
    </form>
  );
}
